import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteSegmentItem,
  getSegmentsList,
  handlePaginate,
  resetSegmentsState,
  SegmentItem,
} from "store/segments/slice";
import { segmentsListLimit, segmentsListLimits } from "configs/limits";
import { ModalTypes, FilterItemTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { usePermissionHelper } from "helpers/usePermissionHelper";

import ActionBtn from "components/buttons/ActionBtn";
import Filter from "components/global/Filter/Filter";

import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import gridIcon from "assets/icons/grid-icon.svg";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const SegmentsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeSegmentId = searchParams.get("entityId");

  const permissions = usePermissionHelper();
  const canRead = permissions?.read ?? false;
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canDelete = permissions?.delete ?? false;

  const segmentsList = useAppSelector((store) => store.segments.list);
  const isListFetched = useAppSelector((store) => store.segments.isListFetched);
  const totalItems = useAppSelector((store) => store.segments.totalItems);
  const totalPages = useAppSelector((store) => store.segments.totalPages);
  const activePage = useAppSelector((store) => store.segments.activePage);
  const processingItemId = useAppSelector(
    (store) => store.segments.processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({ sliceName: 'segments', isListFetched });

  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);
  const [search, setSearch] = useState<string>("");
  const [currentLimit, setCurrentLimit] = useState(segmentsListLimit);

  const [order, setOrder] = useState<{
    title: string;
    countOfUsers: string;
    isActive: string;
  }>({
    title: "none",
    countOfUsers: "none",
    isActive: "none",
  });

  useEffect(() => {
    dispatch(resetSegmentsState());
    dispatch(handlePaginate(1));
    setSearch("");
    setOrder({
      title: "none",
      countOfUsers: "none",
      isActive: "none",
    });

  }, [location.pathname]);

  useEffect(() => {
    if (canRead && !isListFetched) {
      dispatch(
        getSegmentsList({
          limit: currentLimit,
          offset: activePage - 1,
          isAuto: location.pathname.includes("auto"),
          title: search,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          order,
        }),
      );
    }
  }, [canRead, isListFetched, activePage, currentLimit]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOpenCreateModal = () => {
    if (canCreate) {
      navigate(`${location.pathname}?activeModal=${ModalTypes.segment}`);
      dispatch(openModal({ activeModal: ModalTypes.segment }));
    }
  };
  const handleOpenEditModal = (id: number) => {
    if (!canRead) return;
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.segment}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.segment }));
  };

  const handleDeleteItem = async (id: number) => {
    if (!canDelete) return;

    const resultTag = await dispatch(deleteSegmentItem(id ?? 0));
    if (resultTag?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleClearFilters = () => {
    setSearch("");
    dispatch(handlePaginate(1));
    setActive([{ value: 3, label: "All" }]);
  };

  const handleApplyActive = (
    e: { value: string | number; label: string }[],
  ) => {
    setActive(e);
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <Filter
        className="segments-filter"
        handleClear={handleClearFilters}
        handleApply={handleApply}
        filterItems={[
          {
            type: FilterItemTypes.input,
            title: t("title"),
            boxTitle: t("title"),
            icon: gridIcon,
            inputValue: search,
            setInputValue: setSearch,
          },
          {
            type: FilterItemTypes.radio,
            title: t("isActive"),
            boxTitle: t("isActive"),
            icon: gridIcon,
            itemsArray: [
              {
                label: "Active",
                value: 1,
              },
              {
                label: "Inactive",
                value: 0,
              },
              {
                label: "All",
                value: 3,
              },
            ],
            activeItems: active,
            handleApply: handleApplyActive,
          },
        ]}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          {canCreate && (
            <div className="page-list-header--box">
              <ActionBtn
                txt={t("addSegment")}
                handleClick={handleOpenCreateModal}
              />
            </div>
          )}
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={7} rowsCount={15} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {segmentsList.length === 0 && (
              <EmptyState message={t("noSegmentsFound")} />
            )}

            {segmentsList.length > 0 && (
              <Table
                data={segmentsList as Array<SegmentItem & { id: number }>}
                idField="id"
                order={order}
                hasCheckbox={false}
                hasEdit={canUpdate}
                hasView={canRead && !canUpdate}
                hasDelete={canDelete}
                hasOptions={canRead || canUpdate || canDelete}
                onDelete={canDelete ? (id) => handleDeleteItem(id) : undefined}
                onEdit={canUpdate ? (id) => handleOpenEditModal(id) : undefined}
                onView={
                  canRead && !canUpdate
                    ? (id) => handleOpenEditModal(id)
                    : undefined
                }
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                activeRowId={activeSegmentId}
                processingId={processingItemId}
              />
            )}
          </div>
        )}

        {isListFetched && segmentsList.length > 0 && (
          <Pagination
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={segmentsListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              dispatch(handlePaginate(1));
            }}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default SegmentsList;
